import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const VaultWarningContext = createContext(null);

export function VaultWarningProvider({ children }) {
  const [warningState, setWarningState] = useState({ isWarning: false, isOpen: false });
  const [pulseSpeed, setPulseSpeed] = useState(0.8);
  const [pulseIntensity, setPulseIntensity] = useState(0.5); // default 50%
  const [alertPulseSpeed, setAlertPulseSpeed] = useState(2.0);
  const [alertPulseIntensity, setAlertPulseIntensity] = useState(0.5); // default 50%
  const [pulseClosingColor, setPulseClosingColor] = useState("239, 68, 68");   // red
  const [pulseOpeningColor, setPulseOpeningColor] = useState("52, 211, 153");  // green

  // Fetch vault pulse speed on mount and apply it as a CSS custom property
  useEffect(() => {
    let mounted = true;
    api.get("/settings/pulse-speed")
      .then(({ data }) => {
        if (!mounted) return;
        const speed = data.pulse_speed ?? 0.8;
        setPulseSpeed(speed);
        document.documentElement.style.setProperty("--vault-pulse-speed", `${speed}s`);
      })
      .catch(() => {
        // Fall back to default — CSS var fallback already handles this
      });
    return () => { mounted = false; };
  }, []);

  // Fetch vault pulse intensity on mount and apply it as a CSS custom property
  useEffect(() => {
    let mounted = true;
    api.get("/settings/pulse-intensity")
      .then(({ data }) => {
        if (!mounted) return;
        const intensity = data.pulse_intensity ?? 0.5;
        setPulseIntensity(intensity);
        document.documentElement.style.setProperty("--vault-pulse-intensity", String(intensity));
      })
      .catch(() => {
        // Fall back to default
      });
    return () => { mounted = false; };
  }, []);

  // Fetch alert pulse speed on mount and apply it as a CSS custom property
  useEffect(() => {
    let mounted = true;
    api.get("/settings/alert-pulse-speed")
      .then(({ data }) => {
        if (!mounted) return;
        const speed = data.alert_pulse_speed ?? 2.0;
        setAlertPulseSpeed(speed);
        document.documentElement.style.setProperty("--alert-pulse-speed", `${speed}s`);
      })
      .catch(() => {
        // Fall back to default
      });
    return () => { mounted = false; };
  }, []);

  // Fetch alert pulse intensity on mount and apply it as a CSS custom property
  useEffect(() => {
    let mounted = true;
    api.get("/settings/alert-pulse-intensity")
      .then(({ data }) => {
        if (!mounted) return;
        const intensity = data.alert_pulse_intensity ?? 0.5;
        setAlertPulseIntensity(intensity);
        document.documentElement.style.setProperty("--alert-pulse-intensity", String(intensity));
      })
      .catch(() => {
        // Fall back to default
      });
    return () => { mounted = false; };
  }, []);

  // Keep the CSS variable in sync whenever pulseSpeed changes
  useEffect(() => {
    document.documentElement.style.setProperty("--vault-pulse-speed", `${pulseSpeed}s`);
  }, [pulseSpeed]);

  // Keep the CSS variable in sync whenever pulseIntensity changes
  useEffect(() => {
    document.documentElement.style.setProperty("--vault-pulse-intensity", String(pulseIntensity));
  }, [pulseIntensity]);

  // Keep the CSS variable in sync whenever alertPulseSpeed changes
  useEffect(() => {
    document.documentElement.style.setProperty("--alert-pulse-speed", `${alertPulseSpeed}s`);
  }, [alertPulseSpeed]);

  // Keep the CSS variable in sync whenever alertPulseIntensity changes
  useEffect(() => {
    document.documentElement.style.setProperty("--alert-pulse-intensity", String(alertPulseIntensity));
  }, [alertPulseIntensity]);

  // Fetch pulse closing color on mount and apply it as a CSS custom property
  useEffect(() => {
    let mounted = true;
    api.get("/settings/pulse-closing-color")
      .then(({ data }) => {
        if (!mounted) return;
        const color = data.pulse_closing_color ?? "239, 68, 68";
        setPulseClosingColor(color);
        document.documentElement.style.setProperty("--vault-pulse-closing-color", color);
      })
      .catch(() => {
        // Fall back to default
      });
    return () => { mounted = false; };
  }, []);

  // Fetch pulse opening color on mount and apply it as a CSS custom property
  useEffect(() => {
    let mounted = true;
    api.get("/settings/pulse-opening-color")
      .then(({ data }) => {
        if (!mounted) return;
        const color = data.pulse_opening_color ?? "52, 211, 153";
        setPulseOpeningColor(color);
        document.documentElement.style.setProperty("--vault-pulse-opening-color", color);
      })
      .catch(() => {
        // Fall back to default
      });
    return () => { mounted = false; };
  }, []);

  // Keep the CSS variable in sync whenever pulseClosingColor changes
  useEffect(() => {
    document.documentElement.style.setProperty("--vault-pulse-closing-color", pulseClosingColor);
  }, [pulseClosingColor]);

  // Keep the CSS variable in sync whenever pulseOpeningColor changes
  useEffect(() => {
    document.documentElement.style.setProperty("--vault-pulse-opening-color", pulseOpeningColor);
  }, [pulseOpeningColor]);

  const contextValue = useMemo(
    () => ({
      warningState,
      setWarningState,
      pulseSpeed,
      setPulseSpeed,
      pulseIntensity,
      setPulseIntensity,
      alertPulseSpeed,
      setAlertPulseSpeed,
      alertPulseIntensity,
      setAlertPulseIntensity,
      pulseClosingColor,
      setPulseClosingColor,
      pulseOpeningColor,
      setPulseOpeningColor,
    }),
    [warningState, pulseSpeed, pulseIntensity, alertPulseSpeed, alertPulseIntensity, pulseClosingColor, pulseOpeningColor]
  );

  return (
    <VaultWarningContext.Provider value={contextValue}>
      {warningState.isWarning && (
        <div
          aria-hidden="true"
          className={`fixed inset-0 z-50 pointer-events-none ${
            warningState.isOpen
              ? "animate-vault-warn-close-full"
              : "animate-vault-warn-open-full"
          }`}
        />
      )}
      {children}
    </VaultWarningContext.Provider>
  );
}

export function useVaultWarning() {
  return useContext(VaultWarningContext);
}
